import React from 'react';
import { X, Flame } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface WildfireProperties {
  state: string | null;
  county: string | null;
  risk_mean: number | null;
  risk_max: number | null;
}

interface Props {
  county: WildfireProperties;
  onClose: () => void;
}

function categoryFor(mean: number | null): { label: string; color: string } {
  if (mean == null || !isFinite(mean)) return { label: 'Unknown', color: '#6b7280' };
  if (mean < 1.5) return { label: 'Very Low', color: '#16a34a' };
  if (mean < 2.5) return { label: 'Low', color: '#84cc16' };
  if (mean < 3.5) return { label: 'Moderate', color: '#eab308' };
  if (mean < 4.5) return { label: 'High', color: '#f97316' };
  return { label: 'Very High', color: '#dc2626' };
}

export const WildfireDetail: React.FC<Props> = ({ county, onClose }) => {
  const { theme } = useTheme();
  const mean = county.risk_mean != null ? Number(county.risk_mean) : null;
  const max = county.risk_max != null ? Number(county.risk_max) : null;
  const cat = categoryFor(mean);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Flame size={18} color={cat.color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Wildfire Risk</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: cat.color + '15', border: `1px solid ${cat.color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: cat.color, marginBottom: '4px' }}>
            {county.county ? `${county.county}, ${county.state}` : 'County'} — {cat.label}
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            USFS Wildfire Hazard Potential (WHP) integrates fuel, weather, topography, and historic ignitions
            into a 0–5 score. This is a <strong>county mean</strong>; individual parcels may vary.
          </div>
        </div>

        <Section title="County Score">
          <Field label="Category" value={cat.label} />
          {mean != null && <Field label="Mean WHP" value={mean.toFixed(2)} />}
          {max != null && <Field label="Max WHP" value={max.toFixed(2)} />}
        </Section>

        <Section title="BTM Implications">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
            <ImpactItem
              label="Property Insurance"
              detail={cat.label === 'High' || cat.label === 'Very High'
                ? 'Wildfire-category rates add ~$5K–$20K per $1M of coverage. Some carriers decline; fronting + reinsurance common.'
                : 'Minimal premium impact expected. Document defensible-space design at closing.'}
              theme={theme}
            />
            <ImpactItem
              label="Defensible Space"
              detail="BTM pads should maintain a 100-ft non-combustible buffer (gravel, managed vegetation). Add fire access road, hydrants or water tank, and a site FMP. Incremental cost: $50K–$200K."
              theme={theme}
            />
            <ImpactItem
              label="Cooling Water Demand"
              detail="Closed-loop gas-to-wire BTM uses minimal water; risk is from atmospheric drift and falling embers on equipment, not cooling tower loss."
              theme={theme}
            />
            <ImpactItem
              label="Operational Interruption"
              detail="Public-power shutoffs (PSPS) are a grid-side problem. Off-grid BTM is insulated — but smoke can foul air-cooled condensers and derate generation."
              theme={theme}
            />
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            USFS Rocky Mountain Research Station · Wildfire Hazard Potential 2023.
            Aggregated per-county from 270m raster.
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
